import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useToast, humanizeError } from '../shared/Toast'

const todayISO = () => new Date().toLocaleDateString('en-CA')

/** BP above 140/90 is flagged amber throughout the app. */
export function bpFlag(sys, dia) {
  if (sys == null && dia == null) return null
  if ((sys != null && sys > 140) || (dia != null && dia > 90)) return 'high'
  return 'ok'
}

export default function VitalsForm({ recipientId, onDone, onCancel }) {
  const { user } = useAuth()
  const toast = useToast()
  const [f, setF] = useState({
    log_date: todayISO(),
    bp_systolic: '',
    bp_diastolic: '',
    fluids_ml: '',
    weight_kg: '',
    blood_glucose: '',
    notes: '',
  })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF((c) => ({ ...c, [k]: v }))

  const num = (v, float = false) => {
    if (v === '' || v == null) return null
    const n = float ? parseFloat(v) : parseInt(v, 10)
    return Number.isFinite(n) ? n : null
  }

  async function save() {
    const payload = {
      care_recipient_id: recipientId,
      logged_by: user.id,
      log_date: f.log_date || todayISO(),
      bp_systolic: num(f.bp_systolic),
      bp_diastolic: num(f.bp_diastolic),
      fluids_ml: num(f.fluids_ml),
      weight_kg: num(f.weight_kg, true),
      blood_glucose: num(f.blood_glucose, true),
      notes: f.notes.trim() || null,
    }

    const hasAny = ['bp_systolic', 'bp_diastolic', 'fluids_ml', 'weight_kg', 'blood_glucose']
      .some((k) => payload[k] != null)
    if (!hasAny) return toast.error('Enter at least one reading.')

    // A single BP number on its own is ambiguous — ask for the pair.
    const sysOnly = payload.bp_systolic != null && payload.bp_diastolic == null
    const diaOnly = payload.bp_diastolic != null && payload.bp_systolic == null
    if (sysOnly || diaOnly) return toast.error('Enter both blood pressure numbers.')

    setBusy(true)
    const { error } = await supabase.from('vitals_logs').insert(payload)
    setBusy(false)
    if (error) return toast.error(humanizeError(error))
    toast.success('Vitals saved.')
    onDone?.()
  }

  const flag = bpFlag(num(f.bp_systolic), num(f.bp_diastolic))

  return (
    <div className="stack">
      <div className="spread">
        <h2 className="title" style={{ fontSize: 22 }}>Log vitals</h2>
        <button
          onClick={onCancel}
          aria-label="Close"
          style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 24, lineHeight: 1 }}
        >
          ×
        </button>
      </div>

      <div className="field">
        <label className="label" htmlFor="v-date">Date</label>
        <input
          id="v-date" type="date" className="input"
          value={f.log_date} onChange={(e) => set('log_date', e.target.value)}
        />
      </div>

      <div className="field">
        <span className="label">Blood pressure</span>
        <div className="row" style={{ gap: 10 }}>
          <input
            className="input" type="number" inputMode="numeric" placeholder="Systolic"
            aria-label="Systolic" value={f.bp_systolic}
            onChange={(e) => set('bp_systolic', e.target.value)}
          />
          <span className="muted" style={{ fontWeight: 700 }}>/</span>
          <input
            className="input" type="number" inputMode="numeric" placeholder="Diastolic"
            aria-label="Diastolic" value={f.bp_diastolic}
            onChange={(e) => set('bp_diastolic', e.target.value)}
          />
        </div>
        {flag === 'high' && (
          <span className="badge badge-amber" style={{ marginTop: 4 }}>
            Above 140/90 — worth mentioning to the GP
          </span>
        )}
      </div>

      <div className="field">
        <label className="label" htmlFor="v-fluids">Fluids today (ml)</label>
        <input
          id="v-fluids" className="input" type="number" inputMode="numeric" placeholder="e.g. 1200"
          value={f.fluids_ml} onChange={(e) => set('fluids_ml', e.target.value)}
        />
      </div>

      <div className="field">
        <label className="label" htmlFor="v-weight">
          Weight (kg) <span className="hint">— optional</span>
        </label>
        <input
          id="v-weight" className="input" type="number" inputMode="decimal" step="0.1" placeholder="e.g. 62.5"
          value={f.weight_kg} onChange={(e) => set('weight_kg', e.target.value)}
        />
      </div>

      <div className="field">
        <label className="label" htmlFor="v-glucose">
          Blood glucose (mmol/L) <span className="hint">— optional</span>
        </label>
        <input
          id="v-glucose" className="input" type="number" inputMode="decimal" step="0.1" placeholder="e.g. 6.4"
          value={f.blood_glucose} onChange={(e) => set('blood_glucose', e.target.value)}
        />
      </div>

      <div className="field">
        <label className="label" htmlFor="v-notes">Notes</label>
        <textarea
          id="v-notes" className="textarea" placeholder="Anything worth remembering about these readings"
          value={f.notes} onChange={(e) => set('notes', e.target.value)}
        />
      </div>

      <button className="btn btn-block btn-lg" disabled={busy} onClick={save}>
        {busy ? <span className="spinner" /> : 'Save vitals'}
      </button>
    </div>
  )
}
